import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../config/supabase';

type DistanceUnit = 'mi' | 'km';

interface DistanceUnitContextType {
  distanceUnit: DistanceUnit;
  toggleDistanceUnit: () => void;
  formatDistance: (meters: number) => string;
}

const DistanceUnitContext = createContext<DistanceUnitContextType | undefined>(undefined);

export const DistanceUnitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(() => {
    const saved = localStorage.getItem('distanceUnit');
    return (saved === 'km' ? 'km' : 'mi') as DistanceUnit;
  });

  // Sync with user profile when available
  useEffect(() => {
    if (profile?.preferred_distance_unit) {
      // Convert database format ('miles') to app format ('mi')
      const unit = profile.preferred_distance_unit === 'miles' ? 'mi' : 'km';
      setDistanceUnit(unit);
      localStorage.setItem('distanceUnit', unit);
    }
  }, [profile?.preferred_distance_unit]);

  // Update localStorage and database when preference changes
  useEffect(() => {
    localStorage.setItem('distanceUnit', distanceUnit);
    
    // Update database if user is logged in
    const updateDatabasePreference = async () => {
      if (!user?.id) return;
      
      try {
        // Convert app format ('mi') to database format ('miles')
        const dbUnit = distanceUnit === 'mi' ? 'miles' : 'km';
        
        const { error } = await supabase
          .from('users')
          .update({ preferred_distance_unit: dbUnit })
          .eq('id', user.id);

        if (error) {
          console.error('Error updating distance preference:', error);
        } else {
          console.log('Distance preference saved to database:', dbUnit);
        }
      } catch (error) {
        console.error('Exception updating distance preference:', error);
      }
    };
    
    updateDatabasePreference();
  }, [distanceUnit, user?.id]);

  const toggleDistanceUnit = () => {
    setDistanceUnit(prev => prev === 'mi' ? 'km' : 'mi');
  };

  const formatDistance = (meters: number): string => {
    if (distanceUnit === 'km') {
      const km = meters / 1000;
      if (km < 10) {
        return `${km.toFixed(1)} km`;
      }
      return `${km.toFixed(0)} km`;
    } else {
      const miles = meters * 0.000621371;
      if (miles < 10) {
        return `${miles.toFixed(1)} mi`;
      }
      return `${miles.toFixed(0)} mi`;
    }
  };

  return (
    <DistanceUnitContext.Provider value={{ distanceUnit, toggleDistanceUnit, formatDistance }}>
      {children}
    </DistanceUnitContext.Provider>
  );
};

export const useDistanceUnit = () => {
  const context = useContext(DistanceUnitContext);
  if (context === undefined) {
    throw new Error('useDistanceUnit must be used within a DistanceUnitProvider');
  }
  return context;
};













